<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ApiError } from '../api/client'
import { Button, Field, Input } from '../components/ui'
import { useTitle } from '../lib/hooks'
import { safeInternalPath } from '../lib/paths'
import { useSession } from '../lib/session'

const { status, login } = useSession()
const router = useRouter()
const route = useRoute()
const email = ref('')
const password = ref('')
const error = ref('')
const busy = ref(false)

const from = computed(() => safeInternalPath(route.query.from))

useTitle('登录')

watch(
  status,
  (s) => {
    if (s === 'authenticated') void router.replace(from.value || '/')
  },
  { immediate: true },
)

async function onSubmit(e: Event) {
  e.preventDefault()
  if (busy.value) return
  busy.value = true
  error.value = ''
  try {
    await login(email.value.trim(), password.value)
    await router.replace(from.value || '/')
  } catch (err) {
    if (err instanceof ApiError) {
      error.value = err.code === 'forbidden' ? '该账号没有后台权限' : err.message
    } else {
      error.value = '登录失败'
    }
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <div class="grid min-h-screen lg:grid-cols-[1.05fr_0.95fr]">
    <section class="relative hidden overflow-hidden bg-[var(--color-rail)] px-14 py-14 lg:flex lg:items-center">
      <div
        class="pointer-events-none absolute inset-0"
        style="
          background:
            radial-gradient(ellipse 80% 60% at 20% 20%, rgb(0 194 168 / 0.14), transparent 55%),
            radial-gradient(ellipse 50% 40% at 90% 80%, rgb(37 99 235 / 0.12), transparent 50%);
        "
      />
      <img src="/logo.png" alt="Reellax" class="relative h-12 w-auto" />
    </section>

    <section class="flex items-center justify-center bg-base px-6 py-12">
      <form class="w-full max-w-[340px]" @submit="onSubmit">
        <img src="/logo.png" alt="Reellax" class="mb-8 h-8 w-auto lg:hidden" />
        <h2 class="mb-8 text-[22px] font-semibold tracking-tight">登录</h2>
        <div class="flex flex-col gap-4">
          <Field label="邮箱" required>
            <Input
              v-model="email"
              name="email"
              type="email"
              autocomplete="username"
              required
            />
          </Field>
          <Field label="密码" required>
            <Input
              v-model="password"
              name="password"
              type="password"
              autocomplete="current-password"
              required
            />
          </Field>
          <p v-if="error" role="alert" class="text-[12.5px] text-danger">{{ error }}</p>
          <Button type="submit" variant="primary" :loading="busy" class="mt-1 w-full">登录</Button>
        </div>
      </form>
    </section>
  </div>
</template>
